import { makeAutoObservable } from 'mobx';
import { useState, useEffect } from 'react';

interface ParamsType {
    callback?: (isClickOut: boolean) => void;
}

class State {
    public refElement: HTMLElement | null = null;
    public isClickOutside: boolean = false;

    public constructor(private readonly params: ParamsType) {
        makeAutoObservable(this);
    }

    public handleClick = (event: MouseEvent | TouchEvent): void => {
        if (this.refElement === null) {
            return;
        }
        const target = event.target;

        if (target instanceof HTMLElement) {
            this.isClickOutside = !this.refElement.contains(target);

            if (this.params.callback !== undefined) {
                this.params.callback(this.isClickOutside);
            }
        } else {
            console.error('useClickOutside: Event target is not HTML element');
        }
    };

    public setRef = (refElement: HTMLElement | null): void => {
        this.refElement = refElement;
    };

    public updateCallback = (callback?: (isClickOut: boolean) => void): void => {
        this.params.callback = callback;
    };
}

type SetRefType = (refElement: HTMLElement | null) => void;

// TODO: move to global hooks library
export const useClickOutside = ({ callback }: ParamsType): [SetRefType, boolean] => {
    const [state] = useState(() => new State({ callback }));

    useEffect(() => {
        state.updateCallback(callback);
    }, [callback, state]);

    useEffect(() => {
        window.addEventListener('click', state.handleClick);
        document.addEventListener('touchstart', state.handleClick);
        document.addEventListener('mousedown', state.handleClick);

        return (): void => {
            window.removeEventListener('click', state.handleClick);
            document.removeEventListener('mousedown', state.handleClick);
            document.removeEventListener('touchstart', state.handleClick);
        };
    }, [state.handleClick]);

    return [state.setRef, state.isClickOutside];
};
